import React, { useLayoutEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

export type FindHighlightsSettings = {
    highlightCount: number;
    highlightColor: string;
    topic: string;
};

type FindHighlightsSidebarProps = {
    onBack: () => void;
    onSubmit?: (settings: FindHighlightsSettings) => void;
    isSubmitting?: boolean;
};

const HIGHLIGHT_COUNT_MIN = 1;
const HIGHLIGHT_COUNT_MAX = 20;
const TOPIC_CHAR_LIMIT = 3000;
const TOPIC_MIN_HEIGHT = 56;
const TOPIC_MAX_HEIGHT = 160;

const HIGHLIGHT_COLORS = [
    { id: "yellow", name: "Yellow", value: "#FFF59D" },
    { id: "green", name: "Green", value: "#A5D6A7" },
    { id: "blue", name: "Blue", value: "#90CAF9" },
    { id: "pink", name: "Pink", value: "#F48FB1" },
    { id: "orange", name: "Orange", value: "#FFCC80" },
    { id: "purple", name: "Purple", value: "#CE93D8" },
];

const cardSurfaceSx = {
    backgroundColor: "var(--app-bg-surface)",
    border: "1px solid var(--app-border-subtle)",
    borderRadius: "12px",
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
};

const clampInteger = (value: number, min: number, max: number): number => {
    if (!Number.isFinite(value)) return min;
    return Math.min(max, Math.max(min, Math.round(value)));
};

const sanitizeIntegerInput = (value: string): string =>
    String(value ?? "").replace(/[^\d]/g, "").slice(0, 2);

const normalizeIntegerInput = (raw: string, min: number, max: number, fallback: number): string => {
    const sanitized = sanitizeIntegerInput(raw);
    if (!sanitized) return String(clampInteger(fallback, min, max));
    return String(clampInteger(Number(sanitized), min, max));
};

const FindHighlightsSidebar: React.FC<FindHighlightsSidebarProps> = ({
    onBack,
    onSubmit,
    isSubmitting,
}) => {
    const [highlightCountInput, setHighlightCountInput] = useState<string>("5");
    const [highlightColor, setHighlightColor] = useState<string>(HIGHLIGHT_COLORS[0].value);
    const [topic, setTopic] = useState<string>("");
    const topicRef = useRef<HTMLTextAreaElement | null>(null);

    useLayoutEffect(() => {
        const textarea = topicRef.current;
        if (!textarea) return;
        textarea.style.height = "auto";
        const nextHeight = Math.min(TOPIC_MAX_HEIGHT, Math.max(TOPIC_MIN_HEIGHT, textarea.scrollHeight));
        textarea.style.height = `${nextHeight}px`;
        textarea.style.overflowY = textarea.scrollHeight > TOPIC_MAX_HEIGHT ? "auto" : "hidden";
    }, [topic]);

    const handleCountBlur = () => {
        setHighlightCountInput((prev) =>
            normalizeIntegerInput(prev, HIGHLIGHT_COUNT_MIN, HIGHLIGHT_COUNT_MAX, 5),
        );
    };

    const handleSubmit = () => {
        const normalizedCount = clampInteger(
            Number(normalizeIntegerInput(highlightCountInput, HIGHLIGHT_COUNT_MIN, HIGHLIGHT_COUNT_MAX, 5)),
            HIGHLIGHT_COUNT_MIN,
            HIGHLIGHT_COUNT_MAX,
        );
        const normalizedTopic = topic.slice(0, TOPIC_CHAR_LIMIT);

        setHighlightCountInput(String(normalizedCount));
        setTopic(normalizedTopic);

        onSubmit?.({
            highlightCount: normalizedCount,
            highlightColor,
            topic: normalizedTopic.trim(),
        });
    };

    return (
        <Box
            sx={{
                height: "100%",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                p: "3px",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginLeft: "8px" }}>
                <IconButton size="small" onClick={onBack} sx={{ p: 0.5 }}>
                    <ArrowBackRoundedIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: "var(--app-text-primary)" }}>
                    Find highlights
                </Typography>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflowY: "auto",
                }}
            >
                {isSubmitting ? (
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            display: "flex",
                            flexDirection: "column",
                            gap: "18px",
                            overflowY: "auto",
                            px: "12px",
                            pt: "28px",
                            pb: "10px",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: "14px" }}>
                            <CircularProgress
                                size={24}
                                thickness={4.2}
                                sx={{
                                    color: "#0B93D5",
                                    flexShrink: 0,
                                    mt: "2px",
                                }}
                            />
                            <Box aria-live="polite">
                                <Typography sx={{ fontSize: 15, fontWeight: 500, color: "var(--app-text-primary)" }}>
                                    Finding highlights...
                                </Typography>
                                <Typography sx={{ fontSize: 13, lineHeight: 1.55, color: "var(--app-text-muted)", mt: "2px" }}>
                                    Scanning the transcript for the strongest moments to review and highlight.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflowY: "auto",
                            pr: "2px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                            scrollbarWidth: "thin",
                        }}
                    >
                        <Box sx={{ px: "8px" }}>
                            <Typography sx={{ fontSize: 13, lineHeight: 1.5, color: "var(--app-text-muted)" }}>
                                Let Libretto find the most compelling parts for you.
                            </Typography>
                        </Box>

                        <Box sx={cardSurfaceSx}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: "12px",
                                }}
                            >
                                <Typography sx={{ fontSize: 13, fontWeight: 500, color: "var(--app-text-primary)" }}>
                                    Number of highlights
                                </Typography>
                                <Box
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        minWidth: 44,
                                        minHeight: 32,
                                        px: "10px",
                                        py: "4px",
                                        borderRadius: "8px",
                                        backgroundColor: "var(--app-bg-subtle)",
                                        border: "1px solid var(--app-border-subtle)",
                                    }}
                                >
                                    <Box
                                        component="input"
                                        aria-label="Number of highlights"
                                        value={highlightCountInput}
                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                            setHighlightCountInput(sanitizeIntegerInput(event.target.value))
                                        }
                                        onBlur={handleCountBlur}
                                        inputMode="numeric"
                                        sx={{
                                            width: 20,
                                            p: 0,
                                            border: "none",
                                            outline: "none",
                                            background: "transparent",
                                            color: "var(--app-text-primary)",
                                            textAlign: "right",
                                            fontSize: 14,
                                            fontWeight: 500,
                                            fontFamily: "Inter, sans-serif",
                                            "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                                                WebkitAppearance: "none",
                                                margin: 0,
                                            },
                                            appearance: "textfield",
                                        }}
                                    />
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: "12px",
                                }}
                            >
                                <Typography sx={{ fontSize: 13, fontWeight: 500, color: "var(--app-text-primary)" }}>
                                    Highlight color
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    {HIGHLIGHT_COLORS.map((color) => (
                                        <Box
                                            key={color.id}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => setHighlightColor(color.value)}
                                            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                                                if (event.key === "Enter" || event.key === " ") {
                                                    event.preventDefault();
                                                    setHighlightColor(color.value);
                                                }
                                            }}
                                            sx={{
                                                width: 24,
                                                height: 24,
                                                borderRadius: "50%",
                                                backgroundColor: color.value,
                                                border: "2px solid",
                                                borderColor: highlightColor === color.value
                                                    ? "var(--app-text-primary)"
                                                    : "transparent",
                                                boxShadow: highlightColor === color.value
                                                    ? "none"
                                                    : "inset 0 0 0 1px rgba(0,0,0,0.08)",
                                                cursor: "pointer",
                                                transition: "border-color 0.12s ease",
                                                flexShrink: 0,
                                                outline: "none",
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={cardSurfaceSx}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 600, color: "var(--app-text-primary)" }}>
                                    Topic{" "}
                                    <Box component="span" sx={{ fontWeight: 400, color: "var(--app-text-muted)" }}>
                                        (optional)
                                    </Box>
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => setTopic("")}
                                    disabled={!topic}
                                    sx={{
                                        width: 26,
                                        height: 26,
                                        color: topic ? "var(--app-text-muted)" : "var(--app-text-subtle)",
                                    }}
                                >
                                    <CloseRoundedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Box>

                            <Box
                                sx={{
                                    borderRadius: "10px",
                                    backgroundColor: "var(--app-bg-subtle)",
                                    border: "1px solid var(--app-border-subtle)",
                                    px: "10px",
                                    py: "10px",
                                }}
                            >
                                <Box
                                    component="textarea"
                                    ref={topicRef}
                                    value={topic}
                                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                                        setTopic(event.target.value.slice(0, TOPIC_CHAR_LIMIT))
                                    }
                                    placeholder="Provide more details to influence Libretto's response"
                                    sx={{
                                        width: "100%",
                                        minHeight: `${TOPIC_MIN_HEIGHT}px`,
                                        maxHeight: `${TOPIC_MAX_HEIGHT}px`,
                                        p: 0,
                                        resize: "none",
                                        border: "none",
                                        outline: "none",
                                        background: "transparent",
                                        color: "var(--app-text-primary)",
                                        fontSize: 13,
                                        lineHeight: 1.5,
                                        fontFamily: "Inter, sans-serif",
                                        overflowY: "hidden",
                                        "&::placeholder": {
                                            color: "var(--app-text-muted)",
                                            opacity: 1,
                                        },
                                    }}
                                />
                            </Box>
                        </Box>

                        <Button
                            data-tour="find-highlights-submit"
                            fullWidth
                            onClick={handleSubmit}
                            disabled={Boolean(isSubmitting)}
                            sx={{
                                fontFamily: "Inter",
                                fontSize: "14px",
                                fontWeight: 500,
                                textTransform: "none",
                                color: "#FFFFFF",
                                backgroundColor: "#1A1A1A",
                                borderRadius: "8px",
                                height: "40px",
                                "&:hover": {
                                    backgroundColor: "#2B2B2B",
                                },
                                "&:disabled": {
                                    backgroundColor: "#E5E7EB",
                                    color: "#9CA3AF",
                                },
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                                <Box>Submit</Box>
                            </Box>
                        </Button>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default FindHighlightsSidebar;
